// 전투 코어 연출(공격 자석 링·무방비 표식·처형·공중 연타·폭주)의 순수 로직(소유: VFX). three.js를 쓰지 않아 단위 테스트가 가볍다.
// - FadeTracker: 자석 링 페이드(들어오기 80ms, 나가기 120ms, 대상이 바뀌면 다시 80ms)
// - vulnerableView: 무방비 창(틱) → 표식 불투명도·맥동 주파수·등장 팝(끝이 가까울수록 빨리 깜박여 처형 시점을 알린다)
// - TimedSlots: 수명이 있는 일회성 표식 풀(고정 용량, 가득 차면 가장 오래된 것을 덮어쓴다 → 풀이 자라지 않는다)
// - requestExecuteFlash: 처형 섬광을 공용 FlashLimiter 예산(1초 3회·간격 334ms·1회 100ms) 안에서만 건다
import type { FlashLimiter } from './flash';

export const MAGNET_FADE_IN_MS = 80;
export const MAGNET_FADE_OUT_MS = 120;
/** 처형 섬광: 길이(ms), 세기(0..1, 전체 화면 흰금색 섞임 비율). 각성(1.0)보다 훨씬 약하다 */
export const EXECUTE_FLASH = { ms: 70, strength: 0.3, tag: 1 } as const;

export const clamp01 = (v: number): number => (v < 0 ? 0 : v > 1 ? 1 : v);
export const easeOutCubic = (u: number): number => 1 - (1 - clamp01(u)) ** 3;

/** 0..1 값을 켜지면 inMs, 꺼지면 outMs에 걸쳐 따라가는 페이드. 대상이 바뀌면 restart()로 0에서 다시 시작한다 */
export class FadeTracker {
  value = 0;
  constructor(private readonly inMs = MAGNET_FADE_IN_MS, private readonly outMs = MAGNET_FADE_OUT_MS) {}

  step(dtSec: number, on: boolean): number {
    const rate = dtSec * 1000 / (on ? this.inMs : this.outMs);
    this.value = clamp01(this.value + (on ? rate : -rate));
    return this.value;
  }

  restart(): void { this.value = 0; }
}

export interface VulnerableView {
  /** 표식 불투명도(0..1) */
  alpha: number;
  /** 맥동 주파수(Hz). 끝이 가까우면 빨라진다 */
  hz: number;
  /** 글리프 크기 배율(등장 팝: 0.4 → 1.25 → 1) */
  pop: number;
}

/** 팝 길이(틱, 150ms)와 마지막 경고 구간(틱, 400ms)·꺼지는 길이(틱, 130ms) */
export const VULN = { popTicks: 9, urgentTicks: 24, fadeTicks: 8, hz: 2.6, urgentHz: 6.5 } as const;

/** ticksLeft: 남은 무방비 틱(guardBroken), total: 처음 길이(POSTURE.breakTicks). out은 재사용한다(할당 없음) */
export function vulnerableView(ticksLeft: number, total: number, out: VulnerableView): VulnerableView {
  const elapsed = Math.max(0, total - ticksLeft);
  const popU = clamp01(elapsed / VULN.popTicks);
  // 0.4에서 시작해 1.25까지 부풀었다가 1로 정착(과슈트 팝)
  out.pop = popU < 0.65 ? 0.4 + (popU / 0.65) * 0.85 : 1.25 - ((popU - 0.65) / 0.35) * 0.25;
  out.alpha = clamp01(popU * 1.6) * clamp01(ticksLeft / VULN.fadeTicks);
  out.hz = ticksLeft <= VULN.urgentTicks ? VULN.urgentHz : VULN.hz;
  return out;
}

/**
 * 처형 섬광 요청(우선 섬광, c015). 섬광 약화면 걸지 않는다. 공용 리미터가 지금 막으면 false지만 버리지 않고 대기시켜
 * PRIORITY_WAIT_MS 안에 허락되는 첫 프레임에 건다(스킬 화면 펄스 직후의 처형 섬광이 사라지지 않게). 한 번에 한 번만 걸린다
 */
export function requestExecuteFlash(limiter: FlashLimiter, realMs: number, reduce: boolean): boolean {
  if (reduce) return false;
  return limiter.request(realMs, EXECUTE_FLASH.ms, EXECUTE_FLASH.strength, EXECUTE_FLASH.tag, true);
}

/**
 * 수명 있는 일회성 표식 풀. 고정 용량 SoA 배열이라 프레임 루프에서 할당이 없다.
 * delay(초)만큼 늦게 나타나는 표식은 age가 음수에서 시작한다(visible()이 false). 가득 차면 진행률이 가장 큰(가장 오래된) 슬롯을 덮어쓴다.
 */
export class TimedSlots {
  n = 0;
  readonly kind: Uint8Array;
  readonly x: Float32Array; readonly y: Float32Array; readonly z: Float32Array;
  readonly size: Float32Array; readonly yaw: Float32Array; readonly aux: Float32Array;
  readonly age: Float32Array; readonly life: Float32Array;

  constructor(readonly cap: number) {
    this.kind = new Uint8Array(cap);
    const f = () => new Float32Array(cap);
    this.x = f(); this.y = f(); this.z = f(); this.size = f(); this.yaw = f(); this.aux = f(); this.age = f(); this.life = f();
  }

  spawn(kind: number, x: number, y: number, z: number, size: number, life: number, yaw = 0, aux = 0, delaySec = 0): void {
    let i = this.n;
    if (i >= this.cap) {
      // 가득 참: 가장 오래된(진행률 최대) 슬롯 교체
      let best = 0, bu = -1;
      for (let k = 0; k < this.n; k++) { const u = this.age[k] / this.life[k]; if (u > bu) { bu = u; best = k; } }
      i = best;
    } else this.n++;
    this.kind[i] = kind; this.x[i] = x; this.y[i] = y; this.z[i] = z; this.size[i] = size; this.yaw[i] = yaw; this.aux[i] = aux;
    this.age[i] = -delaySec; this.life[i] = life;
  }

  /** dt초 진행하고 수명이 끝난 슬롯을 마지막 슬롯과 바꿔 지운다 */
  step(dt: number): void {
    let i = 0;
    while (i < this.n) {
      this.age[i] += dt;
      if (this.age[i] >= this.life[i]) {
        const j = --this.n;
        if (i !== j) {
          this.kind[i] = this.kind[j]; this.x[i] = this.x[j]; this.y[i] = this.y[j]; this.z[i] = this.z[j];
          this.size[i] = this.size[j]; this.yaw[i] = this.yaw[j]; this.aux[i] = this.aux[j]; this.age[i] = this.age[j]; this.life[i] = this.life[j];
        }
        continue;
      }
      i++;
    }
  }

  visible(i: number): boolean { return this.age[i] >= 0; }
  progress(i: number): number { return clamp01(this.age[i] / this.life[i]); }
  clear(): void { this.n = 0; }
}
